import { useMemo, useState, useEffect } from "react";

function prefetchBackImages(models) {
  if (!Array.isArray(models)) return;

  models.forEach((model) => {
    if (model?.back) {
      const img = new Image();
      img.src = model.back;
    }
  });
}

export const useModelSelector = (
  selectedProduct,
  selectionMode = "user",
  modelsData = {},
  personsData = {},
  onSearchCommit
) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [modal, setModal] = useState({ front: "", back: "" });

  const filteredModels = useMemo(() => {
    const gender = selectedProduct?.category === "men" ? "men" : "women";
    const models = Array.isArray(modelsData?.[gender]) ? modelsData[gender] : [];

    if (!debouncedSearchTerm.trim()) return models;
    const lower = debouncedSearchTerm.toLowerCase();
    return models.filter((m) =>
      String(m?.name || m?.title || "").toLowerCase().includes(lower),
    );
  }, [modelsData, debouncedSearchTerm, selectedProduct]);

  const filteredPersons = useMemo(() => {
    const gender = selectedProduct?.category === "men" ? "men" : "women";
    const persons = Array.isArray(personsData?.[gender]) ? personsData[gender] : [];

    if (!debouncedSearchTerm.trim()) return persons;
    const lower = debouncedSearchTerm.toLowerCase();
    return persons.filter((p) =>
      String(p?.name || p?.title || "").toLowerCase().includes(lower),
    );
  }, [personsData, debouncedSearchTerm, selectedProduct]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    if (onSearchCommit) {
      onSearchCommit(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, selectionMode]);

  // Prefetch back images for models when product is selected
  useEffect(() => {
    if (selectionMode === "model") {
      prefetchBackImages(filteredModels);
    } else {
      prefetchBackImages(filteredPersons);
    }
  }, [selectionMode, filteredModels, filteredPersons]);

  const onSearchChange = (e) => {
    setSearchTerm(e?.target?.value || "");
  };

  const openModal = (front, back) => setModal({ front: front || "", back: back || "" });
  const closeModal = () => setModal({ front: "", back: "" });

  return {
    searchTerm,
    onSearchChange,
    filteredModels,
    filteredPersons,
    modal,
    openModal,
    closeModal,
  };
};

export default useModelSelector;
